/**
 * GlobalErrorHandlers - Component that adds global event listeners for debugging
 * 
 * Captures:
 * - beforeunload - page unload/reload events
 * - unhandledrejection - unhandled promise rejections
 * - error - uncaught runtime errors
 * 
 * These help identify what's causing unexpected remounts or reloads
 */
import { useEffect } from 'react';

const GlobalErrorHandlers = () => {
  useEffect(() => {
    // ═══════════════════════════════════════════════════════════════════════════════
    // BEFOREUNLOAD - Logs when page is about to unload (reload, close, navigate away)
    // THIS IS CRITICAL FOR DETECTING PAGE RELOADS
    // ═══════════════════════════════════════════════════════════════════════════════
    const handleBeforeUnload = (e) => {
      const hasCompletionLock = (() => {
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && key.startsWith('assessment_completion_lock_')) {
            return true;
          }
        }
        return false;
      })();
      
      const debugInfo = {
        timestamp: new Date().toISOString(),
        url: window.location.href,
        documentReadyState: document.readyState,
        hasCompletionLock,
        hasCompletionFlag: !!sessionStorage.getItem('mm_assessment_completion_in_progress'),
        hasWindowFlag: !!window.__MM_COMPLETION_IN_PROGRESS,
        stack: new Error().stack
      };
      
      console.warn('🔴 [BEFOREUNLOAD] PAGE IS RELOADING! 🔴', debugInfo);
      
      // AGGRESSIVE: Store debug info in localStorage to survive reload
      localStorage.setItem('mm_debug_beforeunload', JSON.stringify(debugInfo));
      
      // Log warning but DON'T trigger browser dialog
      // The CompletionGuard will handle redirect after reload
      if (hasCompletionLock || window.__MM_COMPLETION_IN_PROGRESS) {
        console.error('🔴🔴🔴 [BEFOREUNLOAD] RELOAD DURING COMPLETION FLOW! 🔴🔴🔴');
        // NOTE: We intentionally DON'T call e.preventDefault() or set e.returnValue
        // because that triggers the annoying browser "Reload site?" dialog.
        // Instead, we let the reload happen and CompletionGuard handles the redirect.
      }
    };
    
    // ═══════════════════════════════════════════════════════════════════════════════
    // UNHANDLED REJECTION - Logs unhandled promise rejections
    // ═══════════════════════════════════════════════════════════════════════════════
    const handleUnhandledRejection = (e) => {
      console.error('[UNHANDLED_REJECTION]', {
        reason: e.reason?.message || e.reason || 'Unknown',
        stack: e.reason?.stack,
        timestamp: new Date().toISOString()
      });
    };
    
    // ═══════════════════════════════════════════════════════════════════════════════
    // ERROR - Logs uncaught runtime errors
    // Suppresses known non-critical errors like ResizeObserver
    // ═══════════════════════════════════════════════════════════════════════════════
    const handleError = (e) => {
      // Suppress ResizeObserver errors - these are harmless browser warnings
      // that occur when ResizeObserver callbacks trigger more layout changes
      if (e.message && e.message.includes('ResizeObserver loop')) {
        // Prevent the error from being displayed
        e.stopImmediatePropagation();
        e.preventDefault();
        return;
      }
      
      console.error('[WINDOW_ERROR]', {
        message: e.message,
        filename: e.filename,
        lineno: e.lineno,
        colno: e.colno,
        timestamp: new Date().toISOString()
      });
    };
    
    // ═══════════════════════════════════════════════════════════════════════════════
    // PAGEHIDE - Alternative to beforeunload, fires more reliably on mobile
    // ═══════════════════════════════════════════════════════════════════════════════
    const handlePageHide = (e) => {
      if (window.__MM_COMPLETION_IN_PROGRESS) {
        console.warn('🔴 [PAGEHIDE] During completion flow!', {
          persisted: e.persisted,
          timestamp: new Date().toISOString()
        });
      }
    };
    
    // Add listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);
    window.addEventListener('pagehide', handlePageHide);
    
    console.log('[GLOBAL_ERROR_HANDLERS] Installed global error handlers');
    
    // Check for debug info from previous reload
    const prevDebug = localStorage.getItem('mm_debug_beforeunload');
    if (prevDebug) {
      console.warn('🔴 [DETECTED] Previous page reload occurred:', JSON.parse(prevDebug));
      // Clear it after logging
      localStorage.removeItem('mm_debug_beforeunload');
    }
    
    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);
  
  return null; // This component doesn't render anything
};

export default GlobalErrorHandlers;
